import { format } from 'date-fns';
import { enUS, uk } from 'date-fns/locale';
import i18next from 'i18next';
import {
  TRANSLATION_KEY,
  SUBSCRIPTION_STATUS,
  DEFAULT_FEATURES,
  VISIBLE_INVOICE_STATUSES,
  INVOICE_TYPE_TEXT,
  INVOICE_TYPE_TO_TX_TYPE,
  TX_TYPE,
} from './constants';
import { DEFAULT_BILLING_CURRENCY, formatBillingAmount, formatCurrencyAmount } from './currency';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const DATE_FNS_LOCALES = { 'uk-UA': uk, 'en-US': enUS };

const getDateLocale = () => DATE_FNS_LOCALES[i18next.language] || enUS;

export function formatDate(date, pattern = 'dd MMM yyyy') {
  if (!date) return '';
  return format(new Date(date), pattern, { locale: getDateLocale() });
}

export function calculateTrialDaysLeft(trialEndedAt, serverTime) {
  if (!trialEndedAt) return null;
  const now = serverTime ? new Date(serverTime) : new Date();
  return Math.max(Math.ceil((new Date(trialEndedAt) - now) / MS_PER_DAY), 0);
}

// Statuses where the client already has a usable plan — no "trial ended" prompt is due.
const PLAN_ACTIVE_STATUSES = new Set([
  SUBSCRIPTION_STATUS.ACTIVE,
  SUBSCRIPTION_STATUS.PAST_DUE,
  SUBSCRIPTION_STATUS.UNPAID,
  SUBSCRIPTION_STATUS.PAUSED,
  SUBSCRIPTION_STATUS.INCOMPLETE,
]);

/**
 * True when the trial is over and no plan has been picked.
 * Covers both the moment the trial lapses (backend may still report TRIALING for a while)
 * and the terminal statuses it is flipped to afterwards.
 */
export function isTrialExpired(apiSubscription, serverTime) {
  if (!apiSubscription) return false;

  const { status, trialEndedAt, endDate } = apiSubscription;
  if (!trialEndedAt || PLAN_ACTIVE_STATUSES.has(status)) return false;

  // A paid period that outlives the trial means the client did subscribe at some point.
  if (endDate && new Date(endDate) > new Date(trialEndedAt)) return false;

  const now = serverTime ? new Date(serverTime) : new Date();
  return new Date(trialEndedAt) <= now;
}

export function formatPrice(variant, currency = DEFAULT_BILLING_CURRENCY) {
  if (!variant) return '';
  return formatCurrencyAmount(variant.amount, currency);
}

export function formatComparisonValue(comparisonValue, row, currency = DEFAULT_BILLING_CURRENCY) {
  if (!comparisonValue) return '—';

  let formattedValue;
  switch (comparisonValue.type) {
    case 'AMOUNT':
      formattedValue = formatCurrencyAmount(comparisonValue.value, currency);
      break;
    case 'REGIONS':
      formattedValue = comparisonValue.value
        ?.map(({ key, amount }) => `${key} ${formatCurrencyAmount(amount, currency)}`)
        .join('; ');
      break;
    case 'TEXT':
      formattedValue = comparisonValue.value;
      break;
    case 'INCLUDED':
      formattedValue = comparisonValue.value === true ? '✓' : '—';
      break;
    default:
      return '—';
  }

  if (!formattedValue) return '—';
  return row?.unit ? `${formattedValue} ${row.unit}` : formattedValue;
}

export function getVisibleComparisonRows(rows = [], billingPeriod) {
  return [...rows]
    .filter(
      ({ visibleForBillingPeriods }) =>
        !visibleForBillingPeriods?.length || visibleForBillingPeriods.includes(billingPeriod)
    )
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
}

export function formatPeriod(variant) {
  if (!variant) return '';
  const { billingPeriodUnit, billingPeriodCount } = variant;
  const isYear = billingPeriodUnit === 'year';
  if (billingPeriodCount === 1) {
    return i18next.t(`${TRANSLATION_KEY}:${isYear ? 'PER_YEAR' : 'PER_MONTH'}`);
  }
  return i18next.t(`${TRANSLATION_KEY}:${isYear ? 'FOR_YEARS' : 'FOR_MONTHS'}`, {
    period: billingPeriodCount,
  });
}

export function formatRenewalInfo(endDate) {
  return endDate ? i18next.t(`${TRANSLATION_KEY}:RENEWS_ON`, { date: formatDate(endDate) }) : '';
}

function parseScheduledChange(raw) {
  if (!raw || raw.status !== 'SCHEDULED') return null;
  const effectiveAt = raw.effectiveAt ? new Date(raw.effectiveAt) : null;
  if (!effectiveAt || effectiveAt <= new Date()) return null;
  return {
    type: raw.type,
    formattedDate: formatDate(effectiveAt),
    toPlanName: raw.tariffPlan?.name || null,
  };
}

export function adaptSubscription(apiSubscription, serverTime, currency = DEFAULT_BILLING_CURRENCY) {
  if (!apiSubscription) return null;

  const { tariffPlan, tariffPlanVariant } = apiSubscription;
  return {
    status: apiSubscription.status,
    planName: tariffPlan?.name || 'Unknown',
    planId: tariffPlan?.id || 'unknown',
    variantId: tariffPlanVariant?.id || null,
    price: formatPrice(tariffPlanVariant, currency),
    priceAmount: tariffPlanVariant?.amount || null,
    period: formatPeriod(tariffPlanVariant),
    renewalInfo: formatRenewalInfo(apiSubscription.endDate),
    scheduledChange: parseScheduledChange(apiSubscription.scheduledChange),
    trialDaysTotal: 14,
    trialDaysLeft: calculateTrialDaysLeft(apiSubscription.trialEndedAt, serverTime),
    trialEndDate: apiSubscription.trialEndedAt || apiSubscription.endDate || null,
    features: DEFAULT_FEATURES,
    balance: apiSubscription.balance?.balance ?? 0,
    balanceText: formatBillingAmount(apiSubscription.balance?.balance ?? 0, currency),
    currency,
  };
}

export function adaptInvoices(apiData) {
  if (!apiData?.rows) return [];

  return apiData.rows
    .filter((invoice) => VISIBLE_INVOICE_STATUSES.has(invoice.status))
    .map((invoice) => ({
      id: invoice.id,
      status: invoice.status,
      amount: parseFloat(invoice.total).toFixed(2),
      description: INVOICE_TYPE_TEXT[invoice.type],
      date: formatDate(invoice.createdAt),
    }));
}

function shortInvoiceId(id) {
  if (!id) return '';
  const str = String(id);
  return str.length > 12 ? `${str.slice(0, 8)}…${str.slice(-4)}` : str;
}

export function adaptInvoicesForTable(apiData, currency = DEFAULT_BILLING_CURRENCY) {
  if (!apiData?.rows) return [];

  return apiData.rows.map((invoice) => {
    const createdAt = invoice.createdAt ? new Date(invoice.createdAt) : null;
    const amountValue = Number(parseFloat(invoice.total)) || 0;
    return {
      id: invoice.id,
      number: invoice.invoiceNumber || invoice.number || shortInvoiceId(invoice.id),
      status: invoice.status,
      type: INVOICE_TYPE_TO_TX_TYPE[invoice.type] || TX_TYPE.INVOICE,
      rawType: invoice.type,
      amountValue,
      amountText: formatBillingAmount(amountValue, invoice.currency || currency),
      dateRaw: createdAt,
      dateText: createdAt ? format(createdAt, 'dd.MM.yyyy') : '',
      timeText: createdAt ? format(createdAt, 'HH:mm') : '',
      pdfUrl: invoice.pdfUrl || invoice.invoicePdfUrl || null,
      raw: invoice,
    };
  });
}
